<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb
          parentRouteName="BookingSelectList"
          :extra-items="[
            {
              text: bookingPresetName,
              to: 'BookingList',
              params: $route.params.id
            },
            '通知設定'
          ]"
        />
      </div>
    </div>

    <div class="email-wrapper wrapper">
      <div class="row bg-white">
        <div class="col-md-2 mail-sidebar pt-3">
          <div class="menu-bar">
            <ul class="menu-items">
              <li
                :class="{
                  active: true,
                  'has-checkbox': true,
                  'has-checkbox--checked': bookingPresetId,
                }"
              >
                <a>通知設定</a>
              </li>
            </ul>
          </div>
        </div>
        <div class="col-md-10 main-view">
          <b-overlay :show="showLoading">
            <b-card>
              <div class="row d-flex mb-4 mb-xl-2">
                <h4 class="col-12 col-xl-6 mb-3 font-weight-bold">
                  {{ bookingPresetName }}
                </h4>
                <div class="col-12 col-xl-6 mb-3">
                  <AppSingleSelect
                    v-model="branch"
                    :options="branchOptions"
                    @input="handleBranchChange"
                  />
                </div>
              </div>

              <b-form-group>
                <b-form-checkbox
                  v-model="notification.dashboard.enabled"
                >
                  啟用主控台分店預約通知
                </b-form-checkbox>
              </b-form-group>

              <b-form-group class="mb-4">
                <b-form-radio-group
                  v-model="staffMode"
                  :disabled="!notification.dashboard.enabled"
                  @input="handleStaffModeChange"
                >
                  <b-form-radio value="all">全部預約</b-form-radio>
                  <b-form-radio value="specific">特定員工預約</b-form-radio>
                </b-form-radio-group>
              </b-form-group>

              <b-table
                striped
                hover
                responsive
                :items="staffs"
                :fields="staffFields"
              >
                <template slot="head(selected)">
                  <b-form-checkbox
                    v-if="isStaffCheckboxEnabled"
                    :checked="isAllStaffSelected"
                    :indeterminate="isStaffIndeterminate"
                    @change="toggleAllStaff"
                  />
                </template>
                <template slot="cell(selected)" slot-scope="data">
                  <b-form-checkbox
                    :checked="selectedStaffIds.includes(data.item.id)"
                    :disabled="!isStaffCheckboxEnabled"
                    @change="(checked) => toggleStaff(data.item.id, checked)"
                  />
                </template>
                <template slot="cell(avatar)" slot-scope="data">
                  <div class="d-flex align-items-center justify-content-center">
                    <img
                      v-if="data.item.avatar_url"
                      :src="data.item.avatar_url"
                      class="thumb-image"
                      alt=""
                    />
                    <b-avatar
                      v-else
                      variant="secondary"
                      size="2rem"
                    />
                  </div>
                </template>
              </b-table>

              <div class="d-flex justify-content-center mt-4">
                <b-button
                  class="mr-3"
                  @click="cancel"
                  variant="outline-danger"
                >
                  返回
                </b-button>
                <b-button
                  @click="submit"
                  variant="success"
                >
                  儲存
                </b-button>
              </div>
            </b-card>
          </b-overlay>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import bookingApi from "@/apis/booking";
import bookingPresetApi from "@/apis/booking-preset";
import { mapGetters } from "vuex";
import PermissionChecker from "@/utils/PermissionChecker";
import * as consts from "@/consts";
import AppSingleSelect from "@/components/AppSingleSelect.vue";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";
import "bootstrap-vue/dist/bootstrap-vue.css";
import _ from "lodash";

const DEFAULT_NOTIFICATION = {
  dashboard: {
    enabled: false,
    staff: ["*"],
  },
};

export default {
  components: {
    AppSingleSelect,
    BreadCrumb,
  },
  computed: {
    consts() {
      return consts;
    },
    ...mapGetters("route", ["routeQuery"]),
    ...mapGetters("general", ["myBranches"]),
    branchOptions() {
      return this.myBranches.map((branch) => ({
        value: branch.id,
        text: `${branch.branch_code} ${branch.name}`,
      }));
    },
    isStaffCheckboxEnabled() {
      return this.notification.dashboard.enabled && this.staffMode === "specific";
    },
    isAllStaffSelected() {
      return (
        this.staffs.length > 0 &&
        this.staffs.every((staff) => this.selectedStaffIds.includes(staff.id))
      );
    },
    isStaffIndeterminate() {
      const selectedCount = this.staffs.filter((staff) =>
        this.selectedStaffIds.includes(staff.id)
      ).length;
      return selectedCount > 0 && selectedCount < this.staffs.length;
    },
  },
  data() {
    return {
      showLoading: false,
      branchId: null,
      branch: {},
      bookingPresetId: null,
      bookingPresetName: "",
      staffMode: "all",
      selectedStaffIds: [],
      notification: _.cloneDeep(DEFAULT_NOTIFICATION),
      staffs: [],
      staffFields: [
        { key: "selected", label: "" },
        { key: "avatar", label: "" },
        { key: "employee_code", label: "員工編號", sortable: true },
        { key: "name", label: "姓名", sortable: true },
      ],
    };
  },
  watch: {
    "$route.params.branch": {
      handler(newBranchId) {
        if (!newBranchId || newBranchId === this.branchId) {
          return;
        }
        this.branchId = newBranchId;
        this.branch = this.branchOptions.find(
          (branch) => branch.value === this.branchId
        ) || {};
        this.fetchData();
      },
    },
  },
  created() {
    if (!this.checkPermission([consts.BOOKING_BRANCH_NOTIFICATION_SETTING])) {
      this.$router.push({ name: "BookingSelectList" });
      return;
    }

    this.bookingPresetId = this.$route.params.id;
    this.branchId = this.$route.params.branch;
    this.branch = this.branchOptions.find(
      (branch) => branch.value === this.branchId
    ) || {};
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    handleBranchChange() {
      const newBranchId = this.branch.value;
      if (newBranchId === this.branchId) {
        return;
      }

      this.$router.replace({
        name: "BookingBranchPresetNotification",
        params: {
          id: this.bookingPresetId,
          branch: newBranchId,
        },
      });
    },
    handleStaffModeChange(mode) {
      if (mode === "all") {
        this.selectedStaffIds = [];
        this.notification.dashboard.staff = ["*"];
      } else {
        this.notification.dashboard.staff = [...this.selectedStaffIds];
      }
    },
    toggleStaff(staffId, checked) {
      if (checked) {
        if (!this.selectedStaffIds.includes(staffId)) {
          this.selectedStaffIds.push(staffId);
        }
      } else {
        this.selectedStaffIds = this.selectedStaffIds.filter((id) => id !== staffId);
      }
      this.notification.dashboard.staff = [...this.selectedStaffIds];
    },
    toggleAllStaff(checked) {
      if (checked) {
        this.selectedStaffIds = this.staffs.map((staff) => staff.id);
      } else {
        this.selectedStaffIds = [];
      }
      this.notification.dashboard.staff = [...this.selectedStaffIds];
    },
    applyNotificationData(branchNotification) {
      if (branchNotification?.dashboard) {
        this.notification = _.defaultsDeep(
          _.cloneDeep(branchNotification),
          _.cloneDeep(DEFAULT_NOTIFICATION)
        );
        const staff = this.notification.dashboard.staff || [];
        if (staff.includes("*")) {
          this.staffMode = "all";
          this.selectedStaffIds = [];
        } else {
          this.staffMode = "specific";
          this.selectedStaffIds = [...staff];
        }
      } else {
        this.notification = _.cloneDeep(DEFAULT_NOTIFICATION);
        this.staffMode = "all";
        this.selectedStaffIds = [];
      }
    },
    async fetchStaffs() {
      const result = await bookingPresetApi.getStaffs(this.bookingPresetId, {
        branch_id: this.branchId,
      });
      this.staffs = (result.staffs || []).filter((staff) => !staff.is_sys_admin);
    },
    async fetchData() {
      if (!this.bookingPresetId || !this.branchId) {
        this.$swal("錯誤", "無法取得讀取 ID", "error");
        return;
      }

      try {
        this.showLoading = true;
        const presetResponse = await bookingApi.getBookingPreset(this.bookingPresetId, {
          branch_id: this.branchId,
        });
        await this.fetchStaffs();

        this.bookingPresetName = presetResponse.data.data.name;

        if (presetResponse.data.data.type !== "wizard") {
          this.$swal("錯誤", "此預約非精靈模式", "error");
          this.cancel();
          return;
        }

        this.applyNotificationData(presetResponse.data.data.branch_config?.notification);
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取資料錯誤", "error");
      } finally {
        this.showLoading = false;
      }
    },
    async submit() {
      try {
        this.showLoading = true;

        const payload = {
          notification: {
            dashboard: {
              enabled: this.notification.dashboard.enabled,
              staff:
                this.staffMode === "all"
                  ? ["*"]
                  : [...this.selectedStaffIds],
            },
          },
        };

        await bookingApi.setupBookingPresetBranchNotification(
          this.bookingPresetId,
          this.branchId,
          payload
        );
        await this.fetchData();
        this.$swal("儲存成功", "", "success");
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "儲存失敗", "error");
      } finally {
        this.showLoading = false;
      }
    },
    cancel() {
      this.$router.push({ name: "BookingSelectList", query: this.routeQuery });
    },
  },
};
</script>

<style lang="scss" scoped>
.menu-items > li {
  cursor: pointer;

  &.has-checkbox {
    &:after {
      content: "\f134";
      font-family: "Material Design Icons";
      font-size: 16px;
    }
  }

  &.has-checkbox.has-checkbox--checked {
    &:after {
      content: "\f133";
      color: green;
    }
  }
}
</style>
